import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Vehiculos = () => {
  const { user } = useAuth();
  const [vehiculos, setVehiculos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVehiculo, setEditingVehiculo] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;

  useEffect(() => {
    fetchVehiculos();
    fetchClientes();
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin]);

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const fetchVehiculos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/vehiculos');
      setVehiculos(response.data);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingVehiculo) {
        await api.put(`/vehiculos/${editingVehiculo.id}`, values);
        message.success('Vehículo actualizado exitosamente');
      } else {
        await api.post('/vehiculos', values);
        message.success('Vehículo creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingVehiculo(null);
      fetchVehiculos();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingVehiculo(record);
    form.setFieldsValue({
      ...record,
      cliente_id: record.cliente_id ? Number(record.cliente_id) : undefined,
      empresa_id: record.empresa_id ? Number(record.empresa_id) : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/vehiculos/${id}`);
      message.success('Vehículo eliminado exitosamente');
      fetchVehiculos();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) =>
        `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`,
    },
    {
      title: 'Marca',
      dataIndex: 'marca',
      key: 'marca',
    },
    {
      title: 'Modelo',
      dataIndex: 'modelo',
      key: 'modelo',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_vehiculo',
      key: 'tipo_vehiculo',
    },
    {
      title: 'Año',
      dataIndex: 'anio',
      key: 'anio',
    },
    {
      title: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
    },
    {
      title: 'Kilometraje',
      dataIndex: 'kilometraje',
      key: 'kilometraje',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa_nombre',
    }] : []),
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este vehículo?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Vehículos"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingVehiculo(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nuevo Vehículo
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={vehiculos}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingVehiculo ? 'Editar Vehículo' : 'Nuevo Vehículo'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingVehiculo(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="cliente_id"
            label="Cliente"
          >
            <Select placeholder="Selecciona un cliente" allowClear>
              {clientes.map((cliente) => (
                <Select.Option key={cliente.id} value={cliente.id}>
                  {cliente.nombre} {cliente.apellido}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="placa"
            label="Placa"
            rules={[{ required: true, message: 'Ingresa la placa' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="marca"
            label="Marca"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="modelo"
            label="Modelo"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="tipo_vehiculo"
            label="Tipo de Vehículo"
          >
            <Input placeholder="Ej. Automóvil, Camioneta, Motocicleta" />
          </Form.Item>
          <Form.Item name="anio" label="Año">
            <InputNumber min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="color" label="Color">
            <Input />
          </Form.Item>
          <Form.Item name="kilometraje" label="Kilometraje">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {isMainAdmin && (
            <Form.Item
              name="empresa_id"
              label="Empresa"
              tooltip="Opcional: Asignar este vehículo a una empresa específica"
            >
              <Select
                placeholder="Selecciona la empresa (opcional)"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {empresas.map((e) => (
                  <Select.Option key={e.id} value={e.id}>
                    {e.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                {editingVehiculo ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingVehiculo(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Vehiculos;

